
import React from 'react';
import { TeacherFormData } from '@/types/teacher.types';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { FormInput } from '@/components/forms/FormInput';

interface TeacherBasicInfoFieldsProps {
  formData: TeacherFormData;
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  validationErrors?: {
    [key: string]: string;
  };
}

const TeacherBasicInfoFields: React.FC<TeacherBasicInfoFieldsProps> = ({
  formData,
  handleChange,
  validationErrors = {}
}) => {
  return <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="firstName">First Name*</Label>
          <Input 
            id="firstName" 
            name="firstName" 
            value={formData.firstName} 
            onChange={handleChange} 
            inputMode="text"
            className={validationErrors.firstName ? "border-red-500" : ""} 
            placeholder="Enter first name"
          />
          {validationErrors.firstName && <p className="text-sm text-red-500">{validationErrors.firstName}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="lastName">Last Name*</Label>
          <Input 
            id="lastName" 
            name="lastName" 
            value={formData.lastName} 
            onChange={handleChange} 
            inputMode="text"
            className={validationErrors.lastName ? "border-red-500" : ""} 
            placeholder="Enter last name" 
          />
          {validationErrors.lastName && <p className="text-sm text-red-500">{validationErrors.lastName}</p>}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="fatherName">Father's Name</Label>
          <Input 
            id="fatherName" 
            name="fatherName" 
            value={formData.fatherName} 
            onChange={handleChange} 
            inputMode="text"
            placeholder="Enter father's name" 
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="idNumber">ID Number</Label>
          <Input 
            id="idNumber" 
            name="idNumber" 
            value={formData.idNumber} 
            onChange={handleChange}
            inputMode="numeric"
            placeholder="Enter ID number" 
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="gender">Gender</Label>
          <Select name="gender" value={formData.gender} onValueChange={value => {
          handleChange({
            target: {
              name: 'gender',
              value
            }
          } as React.ChangeEvent<HTMLSelectElement>);
        }}>
            <SelectTrigger id="gender">
              <SelectValue placeholder="Select gender" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="male">Male</SelectItem>
              <SelectItem value="female">Female</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="position">Position/Title</Label>
          <Input 
            id="position" 
            name="position" 
            value={formData.position} 
            onChange={handleChange} 
            placeholder="Enter position or title" 
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="dateOfBirth">Date of Birth</Label>
          <Input 
            id="dateOfBirth" 
            name="dateOfBirth" 
            type="date" 
            value={formData.dateOfBirth} 
            onChange={handleChange} 
            placeholder="Select date of birth"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="age">Age</Label>
          <Input 
            id="age" 
            name="age" 
            type="number" 
            value={formData.age} 
            onChange={handleChange} 
            readOnly 
            className="bg-zinc-950"
            placeholder="Auto-calculated" 
          />
          <p className="text-xs text-muted-foreground">Auto-calculated from date of birth</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="maritalStatus">Marital Status</Label>
          <Select name="maritalStatus" value={formData.maritalStatus} onValueChange={value => {
          handleChange({
            target: {
              name: 'maritalStatus',
              value
            }
          } as React.ChangeEvent<HTMLSelectElement>);
        }}>
            <SelectTrigger id="maritalStatus">
              <SelectValue placeholder="Select status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="single">Single</SelectItem>
              <SelectItem value="married">Married</SelectItem>
              <SelectItem value="divorced">Divorced</SelectItem>
              <SelectItem value="widowed">Widowed</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="status">Employment Status</Label>
          <Select name="status" value={formData.status || 'Active'} onValueChange={value => {
          handleChange({
            target: {
              name: 'status',
              value
            }
          } as React.ChangeEvent<HTMLSelectElement>);
        }}>
            <SelectTrigger id="status">
              <SelectValue placeholder="Select status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Active">Active</SelectItem>
              <SelectItem value="On Leave">On Leave</SelectItem>
              <SelectItem value="Suspended">Suspended</SelectItem>
              <SelectItem value="Terminated">Terminated</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>;
};

export default TeacherBasicInfoFields;
